import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Campaign as CampaignIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import marketingService, {
  Campaign,
  CampaignPayload,
  CampaignStats,
  CampaignStatus,
  CampaignType,
} from '../../services/marketing.service';
import { StatCard, PageHeader, FilterBar, EmptyState } from '../../components/Marketing';
import {
  Campaign as CampaignOutlineIcon,
  PlayCircleOutline,
  PaidOutlined,
  SavingsOutlined,
} from '@mui/icons-material';

const STATUS_COLORS: Record<CampaignStatus, 'default' | 'warning' | 'success' | 'info' | 'error'> = {
  rascunho: 'default',
  agendada: 'info',
  ativa: 'success',
  pausada: 'warning',
  terminada: 'error',
};

const STATUS_LABELS: Record<CampaignStatus, string> = {
  rascunho: 'Rascunho',
  agendada: 'Agendada',
  ativa: 'Ativa',
  pausada: 'Pausada',
  terminada: 'Terminada',
};

const TYPE_LABELS: Record<CampaignType, string> = {
  lancamento: 'Lançamento',
  cashback: 'Cashback',
  referral: 'Referral',
  evento: 'Evento',
  parceiro: 'Parceiro',
  conteudo: 'Conteúdo',
};

const EMPTY_FORM: CampaignPayload = {
  nome: '',
  tipo: 'lancamento',
  status: 'rascunho',
  descricao: '',
  data_inicio: '',
  data_fim: '',
  orcamento_cve: undefined,
  audiencia: '',
  kpi_alvo: '',
  metrica_principal: 'utilizadores_ativos_semanais',
};

const formatCurrency = (value?: number | null) =>
  value == null ? '—' : `${Math.round(value || 0).toLocaleString('pt-PT')}$`;

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('pt-PT', { timeZone: 'UTC' }) : '—';

export default function Campaigns() {
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<CampaignType | ''>('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [form, setForm] = useState<CampaignPayload>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      setStats(await marketingService.getCampaignStats());
    } catch (e: any) {
      // stats are optional — don't block the page
      setStats(null);
    }
  }, []);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await marketingService.listCampaigns({
        status: statusFilter || undefined,
        tipo: typeFilter || undefined,
        search: search.trim() || undefined,
      });
      setCampaigns(list);
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar campanhas');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, search]);

  useEffect(() => {
    loadStats();
    loadCampaigns();
  }, [loadStats, loadCampaigns]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (c: Campaign) => {
    setEditing(c);
    setForm({
      nome: c.nome,
      tipo: c.tipo,
      status: c.status,
      descricao: c.descricao || '',
      data_inicio: c.data_inicio || '',
      data_fim: c.data_fim || '',
      orcamento_cve: c.orcamento_cve ?? undefined,
      audiencia: c.audiencia || '',
      kpi_alvo: c.kpi_alvo || '',
      metrica_principal: c.metrica_principal || '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.nome.trim()) {
      setToast({ msg: 'Nome é obrigatório', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await marketingService.updateCampaign(editing.id, form);
        setToast({ msg: 'Campanha atualizada', severity: 'success' });
      } else {
        await marketingService.createCampaign(form);
        setToast({ msg: 'Campanha criada', severity: 'success' });
      }
      setDialogOpen(false);
      await Promise.all([loadStats(), loadCampaigns()]);
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro ao guardar', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (c: Campaign, status: CampaignStatus) => {
    try {
      await marketingService.updateStatus(c.id, status);
      setToast({ msg: `Campanha ${STATUS_LABELS[status].toLowerCase()}`, severity: 'success' });
      await Promise.all([loadStats(), loadCampaigns()]);
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro ao atualizar estado', severity: 'error' });
    }
  };

  const remove = async (c: Campaign) => {
    if (!window.confirm(`Eliminar campanha "${c.nome}"?`)) return;
    try {
      await marketingService.deleteCampaign(c.id);
      setToast({ msg: 'Campanha eliminada', severity: 'success' });
      await Promise.all([loadStats(), loadCampaigns()]);
    } catch (e: any) {
      setToast({ msg: e?.message || 'Erro ao eliminar', severity: 'error' });
    }
  };

  const paged = useMemo(
    () => campaigns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [campaigns, page, rowsPerPage]
  );

  return (
    <Box>
      <PageHeader
        title="Campanhas de Marketing"
        subtitle="Campanhas estratégicas: cashback, referral, eventos, lançamentos"
        icon={<CampaignIcon />}
        actions={
          <>
            <Button startIcon={<RefreshIcon />} onClick={loadCampaigns}>Atualizar</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Nova Campanha</Button>
          </>
        }
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
        <StatCard icon={<CampaignOutlineIcon />} label="Total" value={stats?.total ?? '—'} color="primary" />
        <StatCard icon={<PlayCircleOutline />} label="Ativas" value={stats?.ativas ?? '—'} color="success" />
        <StatCard icon={<SavingsOutlined />} label="Orçamento total" value={formatCurrency(stats?.orcamento_total)} color="info" />
        <StatCard icon={<PaidOutlined />} label="Cashback distribuído" value={formatCurrency(stats?.cashback_distribuido)} color="warning" />
      </Box>

      <FilterBar>
        <TextField
          size="small"
          label="Pesquisar"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FormControl size="small">
          <InputLabel>Estado</InputLabel>
          <Select
            label="Estado"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as CampaignStatus | '')}
          >
            <MenuItem value="">Todos</MenuItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small">
          <InputLabel>Tipo</InputLabel>
          <Select
            label="Tipo"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as CampaignType | '')}
          >
            <MenuItem value="">Todos</MenuItem>
            {Object.entries(TYPE_LABELS).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Início</TableCell>
                <TableCell>Fim</TableCell>
                <TableCell align="right">Orçamento</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <EmptyState
                      icon={<CampaignIcon />}
                      title="Sem campanhas ainda"
                      description="Crie a primeira campanha para começar a gerir iniciativas de marketing: cashback, referral, eventos, lançamentos."
                      action={
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                          Nova Campanha
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                paged.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {c.nome}
                      </Typography>
                      {c.descricao && (
                        <Typography variant="caption" color="text.secondary">
                          {c.descricao}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={TYPE_LABELS[c.tipo]} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={STATUS_LABELS[c.status]}
                        color={STATUS_COLORS[c.status]}
                      />
                    </TableCell>
                    <TableCell>{formatDate(c.data_inicio)}</TableCell>
                    <TableCell>{formatDate(c.data_fim)}</TableCell>
                    <TableCell align="right">{formatCurrency(c.orcamento_cve)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {c.status === 'ativa' ? (
                          <Tooltip title="Pausar">
                            <IconButton size="small" onClick={() => setStatus(c, 'pausada')}>
                              <PauseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Ativar">
                            <IconButton
                              size="small"
                              onClick={() => setStatus(c, 'ativa')}
                              disabled={c.status === 'terminada'}
                            >
                              <PlayIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(c)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => remove(c)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={campaigns.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{editing ? 'Editar Campanha' : 'Nova Campanha'}</DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          <Stack spacing={3}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
              <TextField
                autoFocus
                fullWidth
                label="Nome da campanha"
                placeholder="Ex: Semana Pagali Food"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  label="Tipo"
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value as CampaignType })}
                >
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Descrição"
              placeholder="Objetivo e mensagem chave desta campanha"
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>
              <TextField
                fullWidth
                type="date"
                label="Início"
                InputLabelProps={{ shrink: true }}
                value={form.data_inicio || ''}
                onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
              />
              <TextField
                fullWidth
                type="date"
                label="Fim"
                InputLabelProps={{ shrink: true }}
                value={form.data_fim || ''}
                onChange={(e) => setForm({ ...form, data_fim: e.target.value })}
              />
              <TextField
                fullWidth
                type="number"
                label="Orçamento (CVE)"
                value={form.orcamento_cve ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    orcamento_cve: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <TextField
                fullWidth
                label="Audiência alvo"
                placeholder="Ex: Utilizadores Silver+ em Praia"
                value={form.audiencia}
                onChange={(e) => setForm({ ...form, audiencia: e.target.value })}
              />
              <TextField
                fullWidth
                label="KPI alvo"
                placeholder="Ex: 1000 WAU"
                value={form.kpi_alvo}
                onChange={(e) => setForm({ ...form, kpi_alvo: e.target.value })}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <TextField
                fullWidth
                label="Métrica principal"
                placeholder="Ex: utilizadores_ativos_semanais"
                value={form.metrica_principal}
                onChange={(e) => setForm({ ...form, metrica_principal: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={form.status || 'rascunho'}
                  onChange={(e) => setForm({ ...form, status: e.target.value as CampaignStatus })}
                >
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'A guardar…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
